import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import CommonIcons from "@base/assets/icons";
import CommonStyles from "@components/CommonStyles";
import useToggle from "@base/hooks/useToggle";
import { IPackageFacility } from "@base/modules/doctor/doctorExtraInfo";
import { foldVietnamese } from "@base/modules/doctor/doctorProfile";
import SelectionTrigger from "./SelectionTrigger";

interface IProps {
  label: string;
  facilities: IPackageFacility[];
  selected?: IPackageFacility;
  hasError?: boolean;
  /** Set when the patient has just switched service: the clinic is what they choose next. */
  autoOpen?: boolean;
  onAutoOpened?: () => void;
  onSelect: (facility: IPackageFacility) => void;
}

/** The partner's clinic list, which arrives inside the doctor's `extra_info` rather than as sites. */
export default function FacilityPicker({ label, facilities, selected, hasError, autoOpen, onAutoOpened, onSelect }: IProps) {
  //! state
  const { t } = useTranslation();
  const { open: openList, shouldRender: shouldRenderList, toggle: toggleList } = useToggle();
  const [keyword, setKeyword] = useState<string>("");

  //! function
  useEffect(() => {
    if (!autoOpen) {
      return;
    }

    onAutoOpened?.();

    if (!openList) {
      toggleList();
    }
  }, [autoOpen, openList, toggleList, onAutoOpened]);

  const groupedFacilities = useMemo(() => {
    const needle = foldVietnamese(keyword.trim());
    const matched = needle
      ? facilities.filter((facility) => foldVietnamese(`${facility.name ?? ""} ${facility.address ?? ""}`).includes(needle))
      : facilities;

    // The list is long enough that the region headings are what make it readable; the order the
    // partner sent them in is meaningful, so regions are kept in first-seen order.
    return matched.reduce<{ state: string; items: IPackageFacility[] }[]>((groups, facility) => {
      const state = facility.state ?? "";
      const group = groups.find((item) => item.state === state);

      if (group) {
        group.items.push(facility);
      } else {
        groups.push({ state, items: [facility] });
      }

      return groups;
    }, []);
  }, [facilities, keyword]);

  //! render
  // Region headings only help once the clinics span more than one region.
  const hasRegions = new Set(facilities.map((facility) => facility.state ?? "")).size > 1;

  return (
    <div className="flex flex-col gap-2">
      <span className="text-common-maintext text-sm font-medium font-['Inter']">
        {label} <span className="text-common-red500">*</span>
      </span>

      <SelectionTrigger
        title={selected?.name}
        description={selected?.address}
        placeholder={t("packageBooking.chooseFacility")}
        hasError={hasError}
        onClick={toggleList}
      />

      {shouldRenderList && (
        <CommonStyles.ModalConfirmationUI
          open={openList}
          toggle={toggleList}
          type="normal"
          hiddenAction
          width={640}
          title={label}
          content={
            <div className="flex flex-col gap-3">
              <CommonStyles.InputUI
                variant="filled"
                allowClear
                value={keyword}
                placeholder={t("Search")}
                prefix={<CommonIcons.SearchOutlined className="text-common-subtext" />}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => setKeyword(event.target.value)}
              />

              <div className="flex flex-col gap-3 max-h-[420px] overflow-y-auto">
                {groupedFacilities.map((group) => {
                  return (
                    <div key={group.state} className="flex flex-col gap-2">
                      {hasRegions && group.state ? (
                        <span className="text-common-subtext text-xs font-medium font-['Inter']">{group.state}</span>
                      ) : null}

                      {group.items.map((facility) => {
                        const isActive = facility.name === selected?.name;

                        return (
                          <div
                            key={facility.name}
                            onClick={() => {
                              onSelect(facility);
                              toggleList();
                            }}
                            className={`p-3 rounded-lg border cursor-pointer ${
                              isActive ? "border-common-primary bg-common-primary100" : "border-common-divider bg-common-white"
                            }`}
                          >
                            <CommonStyles.TypographyUI use="paragraph" className="text-common-maintext text-sm font-medium font-['Inter'] !mb-0">
                              {facility.name}
                            </CommonStyles.TypographyUI>
                            <CommonStyles.TypographyUI use="paragraph" className="text-common-subtext text-xs font-normal font-['Inter'] !mb-0">
                              {facility.address}
                            </CommonStyles.TypographyUI>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}

                {!groupedFacilities.length ? (
                  <div className="w-full bg-common-background rounded-lg p-3 text-center text-common-subtext text-sm font-normal font-['Inter']">
                    {t("noResult")}
                  </div>
                ) : null}
              </div>
            </div>
          }
        />
      )}
    </div>
  );
}
